import { BASE_URL, type Lang } from '../../snippets';

interface ChatRequestShape {
  model: string;
  system?: string;
  prompt: string;
  temperature?: number;
  maxTokens: number;
  stream: boolean;
}

const ENV = 'PARTNERAI_API_KEY';

export function chatRequestSnippet(lang: Lang, req: ChatRequestShape): string {
  const messages = [
    ...(req.system?.trim() ? [{ role: 'system', content: req.system }] : []),
    { role: 'user', content: req.prompt },
  ];
  const s = (value: string) => JSON.stringify(value);

  switch (lang) {
    case 'curl': {
      const body = {
        model: req.model,
        messages,
        ...(req.temperature !== undefined ? { temperature: req.temperature } : {}),
        max_tokens: req.maxTokens,
        ...(req.stream ? { stream: true } : {}),
      };
      const json = JSON.stringify(body, null, 2).replace(/'/g, "'\\''");
      return [
        `curl ${req.stream ? '-N ' : ''}${BASE_URL}/chat/completions \\`,
        `  -H "Authorization: Bearer $${ENV}" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d '${json.split('\n').join('\n  ')}'`,
      ].join('\n');
    }

    case 'python': {
      const lines = messages.map((m) => `        {"role": ${s(m.role)}, "content": ${s(m.content)}},`);
      return [
        '# pip install openai',
        'import os',
        'from openai import OpenAI',
        '',
        `client = OpenAI(base_url="${BASE_URL}", api_key=os.environ["${ENV}"])`,
        '',
        `${req.stream ? 'stream' : 'response'} = client.chat.completions.create(`,
        `    model=${s(req.model)},`,
        '    messages=[',
        ...lines,
        '    ],',
        ...(req.temperature !== undefined ? [`    temperature=${req.temperature},`] : []),
        `    max_tokens=${req.maxTokens},`,
        ...(req.stream ? ['    stream=True,'] : []),
        ')',
        ...(req.stream
          ? ['for chunk in stream:', '    if chunk.choices:', '        print(chunk.choices[0].delta.content or "", end="")']
          : ['print(response.choices[0].message.content)']),
      ].join('\n');
    }

    case 'node': {
      const lines = messages.map((m) => `    { role: ${s(m.role)}, content: ${s(m.content)} },`);
      return [
        '// npm install openai',
        'import OpenAI from "openai";',
        '',
        `const client = new OpenAI({ baseURL: "${BASE_URL}", apiKey: process.env.${ENV} });`,
        '',
        `const ${req.stream ? 'stream' : 'response'} = await client.chat.completions.create({`,
        `  model: ${s(req.model)},`,
        '  messages: [',
        ...lines,
        '  ],',
        ...(req.temperature !== undefined ? [`  temperature: ${req.temperature},`] : []),
        `  max_tokens: ${req.maxTokens},`,
        ...(req.stream ? ['  stream: true,'] : []),
        '});',
        ...(req.stream
          ? ['for await (const chunk of stream) {', '  process.stdout.write(chunk.choices[0]?.delta?.content ?? "");', '}']
          : ['console.log(response.choices[0].message.content);']),
      ].join('\n');
    }

    case 'php': {
      const php = (value: string) => s(value).replace(/\$/g, '\\$');
      const lines = messages.map((m) => `        ['role' => '${m.role}', 'content' => ${php(m.content)}],`);
      return [
        '<?php',
        '// composer require openai-php/client guzzlehttp/guzzle',
        "require 'vendor/autoload.php';",
        '',
        '$client = OpenAI::factory()',
        `    ->withApiKey(getenv('${ENV}'))`,
        `    ->withBaseUri('${BASE_URL}')`,
        '    ->make();',
        '',
        `$${req.stream ? 'stream' : 'response'} = $client->chat()->${req.stream ? 'createStreamed' : 'create'}([`,
        `    'model' => '${req.model}',`,
        "    'messages' => [",
        ...lines,
        '    ],',
        ...(req.temperature !== undefined ? [`    'temperature' => ${req.temperature},`] : []),
        `    'max_tokens' => ${req.maxTokens},`,
        ']);',
        ...(req.stream
          ? ['foreach ($stream as $chunk) {', "    echo $chunk->choices[0]->delta->content ?? '';", '}']
          : ['echo $response->choices[0]->message->content;']),
      ].join('\n');
    }
  }
}
